/**
 * router/index.ts
 * SPA routes + scoped auth guard.
 *
 * Shell-nested routes render inside AppShell.vue (sidebar + topbar).
 * Public routes (/login, /platform/login) are NOT nested — they render fullscreen.
 *
 * PR-B: session union + resolveNavigation guard wired in (FS-13).
 *   - /platform/login: public top-level route (FS-8.6)
 *   - /platform parent: second AppShell record with meta.platformMode:true (design §3)
 *     AppShell gates loadMe() behind !platformMode so GET /auth/me is never called
 *     with a platform JWT (PR-B guardrail; full platform-mode UI ships in PR-C T11).
 *   - beforeEach: 3-line adapter delegating to pure resolveNavigation()
 *
 * Spec: FS-8.1 (dashboard nested), FS-8.2 (servicios/orders nested),
 *       FS-8.3 (/login top-level), FS-8.4 (catch-all → dashboard),
 *       FS-8.6 (/platform/login top-level), FS-8.7 (/platform/companies nested),
 *       FS-13.1–FS-13.4 (scoped guard matrix).
 *
 * refactor/frontend-router-split (openspec/arquitectura/convencion-modulo.md §4):
 * route definitions now live per-module under router/routes/ and are imported
 * here; this file only composes them (plus createRouter + the auth guard).
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { currentClaims, isAuthenticated, clearSession } from '../lib/session.js';
import { logout } from '../lib/api/identity/auth.js';
import { hasCapability } from '../lib/capabilities.js';
import { resolveNavigation } from './guard.js';
import type { GuardRoute } from './guard.js';
import { publicRoutes } from './routes/public.routes.js';
import { printRoutes } from './routes/print.routes.js';
import { dashboardRoutes, dashboardCatchAllRoute } from './routes/dashboard.routes.js';
import { serviciosRoutes } from './routes/servicios.routes.js';
import { adminRoutes } from './routes/admin.routes.js';
import { comprasRoutes } from './routes/compras.routes.js';
import { rrhhRoutes } from './routes/rrhh.routes.js';
import { sumiRoutes, cuentasPagarLegacyRedirects } from './routes/sumi.routes.js';
import { aplicativosRoutes } from './routes/aplicativos.routes.js';
import { ayudaRoutes } from './routes/ayuda.routes.js';
import { platformRoutes } from './routes/platform.routes.js';

const routes: RouteRecordRaw[] = [
  // --- Public routes (NOT nested under AppShell — render fullscreen) ---
  ...publicRoutes,

  // --- Top-level print routes (no AppShell chrome, window.print() auto) ---
  ...printRoutes,

  // --- Legacy /cuentas-pagar/* redirects (frontend-only, see sumi.routes.ts) ---
  ...cuentasPagarLegacyRedirects,

  // --- Tenant shell-nested routes (render inside AppShell) ---
  // FS-8.1, FS-8.2: dashboard + servicios/orders are siblings under AppShell parent.
  {
    path: '/',
    component: () => import('../components/layout/AppShell.vue'),
    children: [
      ...dashboardRoutes,
      ...serviciosRoutes,
      ...adminRoutes,
      ...comprasRoutes,
      ...rrhhRoutes,
      ...sumiRoutes,
      ...aplicativosRoutes,
      ...ayudaRoutes,
      // Catch-all child MUST stay last — FS-8.4: targets dashboard.
      ...dashboardCatchAllRoute,
    ],
  },

  // --- Platform shell-nested routes (SECOND AppShell parent, design §3) ---
  // meta.platformMode:true tells AppShell to skip loadMe() (PR-B guardrail).
  // Full platform-mode identity display (topbar / sidebar) ships in PR-C (T11).
  // FS-8.7: /platform/companies is nested under the platform AppShell parent.
  {
    path: '/platform',
    component: () => import('../components/layout/AppShell.vue'),
    meta: { platformMode: true },
    children: [...platformRoutes],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// FS-13: scoped guard — delegates to pure resolveNavigation() for the full matrix.
// Gate on isAuthenticated (which checks token EXPIRY) before reading claims:
// currentClaims returns the decoded payload even for an expired token, so an
// expired session must be treated as unauthenticated (→ bounce to login).
router.beforeEach((to) => {
  const claims = isAuthenticated.value ? currentClaims.value : null;

  // acceso.web (2026-10-14): a session opened from the field app is shared with
  // this one; without acceso.web the backend refuses every request from here.
  // Close it and say why on the login screen instead of a page of 403s.
  if (claims?.scope === 'tenant' && !hasCapability(claims.capabilities ?? [], 'acceso.web')) {
    clearSession();
    void logout().catch(() => undefined);
    return { path: '/login', query: { acceso: 'web' } };
  }

  // Build GuardClaims with roles + capabilities for tenant sessions.
  // FS-13.2: requiresRole gate; AOP-7.2: requiresCapability gate.
  const guardClaims = (() => {
    if (!claims) return null;
    if (claims.scope === 'tenant') {
      return {
        scope: 'tenant' as const,
        roles: claims.roles ?? [],
        capabilities: claims.capabilities ?? [],
      };
    }
    return { scope: 'platform' as const };
  })();

  return resolveNavigation(
    {
      path: to.path,
      fullPath: to.fullPath,
      meta: to.meta as GuardRoute['meta'],
    },
    guardClaims,
  );
});
